













































































































































































import {
  IWorkflow,
  IWorkflowState,
  IWorkflowTransition,
  IWorkflowTransitionData,
  IWorkflowTransitionNode,
  SystemError,
  WorkflowTransition,
  WorkflowTransitionNode
} from '@/models';
import { ISystemError } from '@/models/error/error.types';

import {
  computed,
  defineComponent,
  PropType,
  ref,
  watch,
  nextTick,
  reactive
} from '@vue/composition-api';

import { MaybeRef, useVModel } from '@vueuse/core';

import { useWorkFlow } from '../workflow-canvas/composables';
import { PromiseQueue } from '@/models/promise-queue/promise-queue';

import { createNamespacedHelpers, useStore } from 'vuex-composition-helpers';
import { IRootState } from '@/store';
import { ALForm } from '@/common/component.types';
import { VForm } from 'vuetify/lib/components';
import {
  IWorkflowActions,
  IWorkflowGetters,
  IWorkflowMutations,
  IWorkflowStoreState
} from '@/store/workflow';
import { diffDeep, setErrorFromAxiosError } from '@/utils/helpers';
import { debounce } from 'lodash';
import { provideWorkflowStateThresholdGroup } from './composables/useWorkflowStateThresholdGroup';

import WorkflowTransitionForm from './WorkflowTransitionForm.vue';
import WorkflowTransitionDetailOptionButton from './WorkflowTransitionDetailOptionButton.vue';
import WorkflowSnackbar from './WorkflowSnackbar.vue';
import WorkflowTransitionActionsDrawerList from './WorkflowTransitionActionsDrawerList.vue';
import DeleteConfirmationDialog from '../common/DeleteConfirmationDialog.vue';
import WorkflowTransitionPredicateQueryTreeBuilder from './predicates/WorkflowTransitionPredicateQueryTreeBuilder.vue';

export const DRAWER_WIDTH = 600;

export default defineComponent({
  name: 'WorkflowTransitionDetailDrawer',

  components: {
    WorkflowTransitionPredicateQueryTreeBuilder,
    DeleteConfirmationDialog,
    WorkflowSnackbar,
    WorkflowTransitionForm,
    WorkflowTransitionDetailOptionButton,
    WorkflowTransitionActionsDrawerList
  },

  emits: ['deleted-transition'],

  props: {
    value: { type: Boolean },
    workflow: {
      type: Object as PropType<IWorkflow>,
      required: true
    },
    transition: {
      type: Object as PropType<IWorkflowTransition | null>,
      default: () => null
    }
  },

  setup(props, { emit }) {
    provideWorkflowStateThresholdGroup(props);

    const isActive = useVModel(props);

    const { updateEdgeData, disabled } = useWorkFlow();

    const workflowTransitionData = ref<IWorkflowTransitionData | null>(
      props.transition
    );

    const workflowId = computed(() => props.workflow.id!);

    const nodes = computed({
      get: () => {
        return (workflowTransitionData.value?.nodes ?? []).map(
          node => new WorkflowTransitionNode(node)
        );
      },

      set: (value: IWorkflowTransitionNode[]) => {
        workflowTransitionData.value!.nodes = value;
      }
    });

    watch(
      () => props.transition,
      value => {
        workflowTransitionData.value = value;
      },
      {
        deep: true
      }
    );

    const error = ref<ISystemError | null>(null);

    function clearError() {
      error.value = null;
    }

    const isLoading = ref(false);

    function onTransitionDeletedHandler() {
      isActive.value = false;

      emit('deleted-transition', props.transition?.id?.toString());
    }

    function onTransitionUpdateHandler(
      edgeId: string,
      edgeData: IWorkflowTransition
    ) {
      updateEdgeData(edgeId, edgeData, {
        replace: true
      });

      setSnackbar(
        'Updated Transition',
        'Form is automatically saved after typing'
      );
    }

    const store = useStore<IRootState>();

    const { useActions, useGetters } = createNamespacedHelpers<
      IWorkflowStoreState,
      IWorkflowGetters,
      IWorkflowActions,
      IWorkflowMutations
    >(store, 'workflow');

    const { getWorkflowTransitionById } = useGetters([
      'getWorkflowTransitionById'
    ]);

    const { updateWorkflowTransition } = useActions([
      'updateWorkflowTransition'
    ]);

    const originalWorkflowTransition = computed(() =>
      props.transition
        ? getWorkflowTransitionById.value(props.transition.id!)
        : null
    );

    const connectedStates = computed<
      [IWorkflowState | null, IWorkflowState | null]
    >(() => {
      if (!originalWorkflowTransition.value) return [null, null];
      return [
        originalWorkflowTransition.value.source_state as IWorkflowState,
        originalWorkflowTransition.value.target_state as IWorkflowState
      ];
    });

    const isSnackbarActive = ref(false);
    const snackbarData = reactive({
      title: '',
      description: ''
    });

    function setSnackbar(title: string, description = '') {
      snackbarData.title = title;
      snackbarData.description = description;

      nextTick(() => {
        isSnackbarActive.value = true;
      });
    }

    const isComputationFormValid = ref(false);

    const promiseQueue = new PromiseQueue();

    function validateForm(): boolean {
      let valid = false;
      if (conditionFormRef.value) {
        valid = conditionFormRef.value.validate();
      }
      return valid;
    }

    const conditionFormRef = ref<InstanceType<typeof VForm> & ALForm>();

    const debounceSaveWorkflowTransitionNodes = debounce(() => {
      promiseQueue.clearQueue();

      promiseQueue.enqueue(() => saveWorkflowTransitionNodes());
    }, 1000);

    async function saveWorkflowTransitionNodes() {
      clearError();

      const valid = validateForm();

      if (valid) {
        isLoading.value = true;
        let workflowTransitionInstance: IWorkflowTransition | null = null;

        try {
          workflowTransitionInstance = new WorkflowTransition(
            workflowTransitionData.value!
          );
        } catch (err) {
          if (SystemError.isSystemError(err)) {
            error.value = err;
          }
        }
        if (workflowTransitionInstance != null) {
          const payloadDelta = diffDeep(
            workflowTransitionInstance,
            getWorkflowTransitionById.value(workflowTransitionInstance.id!),
            true
          );

          updateWorkflowTransition({
            workflowId: props.workflow.id!,
            transition: { id: payloadDelta.id, nodes: payloadDelta.nodes }
          })
            .then(() => {
              onTransitionUpdateHandler(
                payloadDelta.id!.toString(),
                getWorkflowTransitionById.value(
                  workflowTransitionInstance!.id!
                )!
              );
            })
            .catch(err => {
              error.value = setErrorFromAxiosError(err);
            })
            .finally(() => {
              isLoading.value = false;
            });
        }
      }
    }

    return {
      isComputationFormValid,
      conditionFormRef,

      isActive,
      workflowId,

      workflowTransitionData,
      isLoading,
      DRAWER_WIDTH,

      error,
      clearError,

      connectedStates,
      // workflow functions
      onTransitionUpdateHandler,
      onTransitionDeletedHandler,

      originalWorkflowTransition,

      setSnackbar,

      isSnackbarActive,
      snackbarData,

      nodes,
      disabled,

      debounceSaveWorkflowTransitionNodes
    };
  }
});
